import { type FC, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useMatch } from "react-router";
import { titleCase } from "title-case";

import { useDocumentTitle } from "@/app-utils/hooks/useDocumentTitle.ts";
import { useURLViewID } from "@/app-utils/hooks/useUrl.ts";
import { capitalizeFirstLetter } from "@/app-utils/string.ts";
import { type AdvancedSearchParams, type Group } from "@/graphql.ts";
import { populateAdvancedSearchPanel } from "@/redux/modules/advanced-search.ts";
import { showNavbarElement } from "@/redux/modules/navbar.ts";
import { type ISavedGroupState } from "@/redux/modules/saved-group.ts";
import { type SavedViewState } from "@/redux/modules/saved-view.ts";
import { type ISearchResultState } from "@/redux/modules/search-results.ts";
import { type ITableContextState, TableType } from "@/redux/modules/table-context.ts";
import { createTableSpecForGroupParent, getTableDataKey, type ITableData } from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { FEATURE_DISPLAY_NAME } from "@/settings/settings.ts";
import { getSearchResultPageNavPanelCategory } from "@/ui-modules/core/SearchResultPage.tsx";
import { getSavedGroupPageNavPanelCategory } from "@/ui-modules/entities/SavedGroupPage.tsx";

import { ContextWidgetType } from "./ContextWidget.js";
import ItemPagination from "./ItemPagination.js";
import { type INavPanelCategory } from "./NavigationPanel.js";
import PageLayout from "./PageLayout.js";
import { type IRouteNavItemProps } from "./RouteNavItem.js";

interface IGenericDetailPageLayoutOwnProps {
  // Props given by the parent components, usually *SummaryView
  // The router path is usually /:entity/:entityId/:subEntity
  groupType: string;
  entityId: number;
  groupSubtypeId?: number;

  sidebarNavItems: IRouteNavItemProps[];
  isLoading?: boolean;
  getDisplayName: () => string;
}

/**
 * The GenericDetailPageLayout smart component is called from the specific detail page smart components (i.e. GeneDetailPage).
 * GenericDetailPageLayout encapsulates layout and display logic that is common to all display pages, specifically logic
 * related to breadcrumb generation and item pagination.
 */
const GenericDetailPageLayout: FC<IGenericDetailPageLayoutOwnProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const viewId = useURLViewID();
  const tableDataSpec = createTableSpecForGroupParent(props.groupType, undefined, props.groupSubtypeId);
  const paginationTableData = useSelector<IAppState, ITableData>(
    (state) => state.tableData.tables[getTableDataKey(tableDataSpec)],
  );
  const tableContext = useSelector<IAppState, ITableContextState>((state) => state.tableContext);
  const searchResultState = useSelector<IAppState, ISearchResultState>((state) => state.searchResults);
  const savedGroupState = useSelector<IAppState, ISavedGroupState>((state) => state.savedGroup);
  const viewState = useSelector<IAppState, SavedViewState>((state) => state.savedView);

  // TODO: Should take the props
  const match = useMatch("/:entity/:entityId/:subEntity");
  const location = useLocation();

  useDocumentTitle(`${props.getDisplayName() || ""} [${capitalizeFirstLetter(FEATURE_DISPLAY_NAME[props.groupType])}]`);

  const navPanelCategories = useMemo(() => {
    const openAdvancedSearchPanel = (params: AdvancedSearchParams) => {
      dispatch(populateAdvancedSearchPanel(params));
      dispatch(showNavbarElement({ showAdvancedSearchPanel: true }));
    };
    const categories: INavPanelCategory[] = [];
    const paginationItems: number[] = paginationTableData?.rowIds ?? [];
    const activePaginationItem = props.entityId && paginationItems.find((itemId: number) => itemId === props.entityId);

    let itemPagination;
    // The tableContext is set to either SavedGroup or SearchResult if we need to show a ItemPagination -
    // we should NOT show the ItemPagination when we clicked on a single global search suggestion and there
    // is still tableData in the state.
    if (
      tableContext.tableType !== TableType.None &&
      paginationItems &&
      paginationItems.length > 0 &&
      activePaginationItem
    ) {
      itemPagination = (
        <ItemPagination
          items={paginationTableData.rowIds}
          selectedItem={activePaginationItem}
          onPageChangeToRoute={({ currentIndex, items }) => {
            if (match) {
              return location.pathname.replace(match?.params.entityId, String(items[currentIndex]));
            }
            throw new Error("Pagination cannot happen outside summary pages.");
          }}
          hasKeyboard
          style={{ marginBottom: 5 }}
        />
      );
    }

    const detailPageCategory: INavPanelCategory = {
      label: titleCase(FEATURE_DISPLAY_NAME[props.groupType]),
      widget: itemPagination,
      navItems: props.sidebarNavItems,
      isLoading: props.isLoading,
    };
    categories.push(detailPageCategory);

    // pagination category
    if (activePaginationItem) {
      switch (tableContext.tableType) {
        case TableType.SearchResults: {
          const searchResultsCategory: INavPanelCategory = getSearchResultPageNavPanelCategory(
            searchResultState,
            openAdvancedSearchPanel,
            true,
          );
          categories.unshift(searchResultsCategory);
          break;
        }
        case TableType.SavedGroup: {
          const savedGroup: Group = savedGroupState?.group;
          const savedGroupCategory: INavPanelCategory = getSavedGroupPageNavPanelCategory(savedGroup, true);
          categories.unshift(savedGroupCategory);
          break;
        }
      }
    }
    return categories;
  }, [
    dispatch,
    location.pathname,
    match,
    paginationTableData?.rowIds,
    props,
    savedGroupState?.group,
    searchResultState,
    tableContext.tableType,
  ]);

  const contextProps = useMemo(() => {
    const paginationItems: number[] = paginationTableData?.rowIds ?? [];
    const activePaginationItem = props.entityId && paginationItems.find((itemId: number) => itemId === props.entityId);

    if (activePaginationItem && tableContext?.tableType === TableType.SavedGroup) {
      return {
        type: ContextWidgetType.SAVED_GROUP,
        data: savedGroupState?.group,
      };
    } else if (viewId && !viewState.isLoading && viewState.view && viewState.view.id === viewId) {
      return {
        type: ContextWidgetType.SAVED_VIEW,
        data: viewState.view,
      };
    }

    return undefined;
  }, [
    paginationTableData?.rowIds,
    props.entityId,
    savedGroupState?.group,
    tableContext?.tableType,
    viewId,
    viewState.isLoading,
    viewState.view,
  ]);

  return (
    <PageLayout contextWidgetProps={contextProps} navPanelCategories={navPanelCategories} isLoading={props.isLoading} />
  );
};

export default GenericDetailPageLayout;
